import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import { useStore } from "@/stores/store-context";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif"];

export const AvatarField = observer(function AvatarField() {
  const { t } = useTranslation();
  const { user } = useStore();
  const currentUser = user.currentUser;

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const previewUrl = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  if (!currentUser) return null;

  const initial = (currentUser.display_name || currentUser.email || "?").charAt(0).toUpperCase();
  const avatarUrl = currentUser.avatar_url;
  const avatarAssetId = currentUser.avatar_asset_id;
  const displaySrc = previewUrl ?? (avatarUrl || null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!next) return;

    if (!ACCEPTED_TYPES.includes(next.type)) {
      toast.error(t("profile_settings.general.avatar_invalid_type"));
      return;
    }
    if (next.size > MAX_AVATAR_BYTES) {
      toast.error(t("profile_settings.general.avatar_too_large"));
      return;
    }

    setPendingFile(next);
    setIsUploading(true);
    try {
      await user.uploadAvatar(next);
      toast.success(t("profile_settings.general.avatar_upload_success"));
    } catch (error) {
      console.error("[profile] avatar upload failed", requestErrorSummary(error));
      toast.error(t("profile_settings.general.avatar_upload_error"));
    } finally {
      setIsUploading(false);
      setPendingFile(null);
    }
  };

  const handleRemove = async () => {
    if (!avatarAssetId) return;
    setIsRemoving(true);
    try {
      await user.removeAvatar(avatarAssetId);
      toast.success(t("profile_settings.general.avatar_remove_success"));
    } catch (error) {
      console.error("[profile] avatar remove failed", requestErrorSummary(error));
      toast.error(t("profile_settings.general.avatar_remove_error"));
    } finally {
      setIsRemoving(false);
    }
  };

  const disabled = isUploading || isRemoving;

  return (
    <div className="flex items-center gap-4">
      {displaySrc ? (
        <img
          src={displaySrc}
          alt={t("profile_settings.general.avatar_alt", { name: currentUser.display_name })}
          className="h-16 w-16 rounded-full border border-border object-cover"
        />
      ) : (
        <div className="grid h-16 w-16 place-items-center rounded-full border border-border bg-muted text-lg font-semibold">
          {initial}
        </div>
      )}
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">
          {t("profile_settings.general.avatar_label")}
        </p>
        <p className="text-xs text-muted-foreground">{t("profile_settings.general.avatar_hint")}</p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={(event) => void handleFileChange(event)}
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
      >
        {isUploading
          ? t("profile_settings.general.avatar_uploading")
          : avatarUrl
            ? t("profile_settings.general.avatar_change")
            : t("profile_settings.general.avatar_upload")}
      </Button>
      {avatarAssetId ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void handleRemove()}
          disabled={disabled}
        >
          {isRemoving
            ? t("profile_settings.general.avatar_removing")
            : t("profile_settings.general.avatar_remove")}
        </Button>
      ) : null}
    </div>
  );
});
